import React, { useEffect, useRef, useState } from "react";
import { Download, Eye } from "lucide-react";
import { Button, buttonVariants } from "../../../shadcn/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../shadcn/components/ui/tooltip";
import StatusAlert from "../StatusAlert";
import { useTranslation } from "../../../context/Internationalization";
import { downloadFileMedia, getFileMedia, isLocalFile } from "./fileMedia";
import FilePreview from "./FilePreview";

export default function FileActions({
  file,
  disabled = false,
  previewEnabled = true,
  children,
}) {
  const { t } = useTranslation();
  const { url, name, type, previewKind, available } = getFileMedia(file);
  const identity = isLocalFile(file) ? file : JSON.stringify([url, name, type]);
  const [preview, setPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    setPreview(false);
    setError(false);
    setDownloading(false);
    inFlight.current = false;
    return () => {
      request.current += 1;
    };
  }, [identity]);

  const download = async (event) => {
    event.stopPropagation();
    if (inFlight.current) return;
    inFlight.current = true;
    const current = request.current;
    setDownloading(true);
    setError(false);
    try {
      await downloadFileMedia(file);
    } catch (_) {
      if (request.current === current) setError(true);
    } finally {
      if (request.current === current) {
        inFlight.current = false;
        setDownloading(false);
      }
    }
  };

  const downloadError = error && (
    <StatusAlert severity="error" className="w-full">
      <div className="[overflow-wrap:anywhere]">
        {t("file_download_failed_help")}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={download}
          disabled={disabled || downloading}
        >
          {t("retry")}
        </Button>
        {/^(https?:|blob:)/i.test(url) && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonVariants({ size: "sm", variant: "outline" })}
          >
            {t("open_file")}
          </a>
        )}
      </div>
    </StatusAlert>
  );

  // The tooltip sits on a wrapper span, as in the MUI version: a disabled
  // button gets no pointer events, so its hint would never show.
  const iconAction = (label, hint, icon, onClick, isDisabled) => (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex" />}>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={isDisabled}
          aria-label={label}
          onClick={onClick}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );

  return (
    <div
      className="flex min-w-0 flex-wrap items-center gap-1"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {available &&
        previewKind &&
        previewEnabled &&
        iconAction(
          `${t("preview_file")} ${name}`,
          t("preview_file"),
          <Eye aria-hidden="true" />,
          () => setPreview(true),
          disabled
        )}
      {available &&
        iconAction(
          `${t("download_file")} ${name}`,
          t("download_file"),
          <Download aria-hidden="true" />,
          download,
          disabled || downloading
        )}
      {children}
      {!preview && downloadError}
      {preview && (
        <FilePreview
          file={file}
          onClose={() => setPreview(false)}
          actions={
            error ? (
              downloadError
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={download}
                disabled={downloading || disabled}
              >
                <Download aria-hidden="true" />
                {t("download_file")}
              </Button>
            )
          }
        />
      )}
    </div>
  );
}
